/**
 * The product's menu of actions (#6559 round 7e, Claude Design).
 *
 * Every menu in the app was a raw MUI `Menu` styled — or not styled — at its
 * call site, so they rendered at MUI's defaults: 16px type, 48px rows, a 4px
 * radius and a material elevation shadow belonging to a surface this product
 * never draws. The fix is not different `sx`; it is that styling stopped being
 * a call-site decision. **This wrapper is deliberately closed**: it takes
 * items, an anchor and an open state, and forwards no `sx`, `slotProps`,
 * `MenuProps` or children. If that door is left open, the menus drift apart
 * again within a year.
 *
 * MUI stays underneath for its portal, focus trap, roving focus and collision
 * flipping — all of which are real work and none of which is a design
 * decision.
 *
 * A menu of actions is not a picker. The dividing line is exact: **a menu
 * closes when you pick; a picker does not.** Everything a picker needs —
 * persistent open state, multi-select, check affordances, a filter field, a
 * scrolling max-height, Apply/Reset — this component refuses. A picker is a
 * separate component that shares the surface tokens and none of the item
 * semantics.
 */
import React from "react";
import {
  Menu as MuiMenu,
  MenuItem,
  Divider,
  ListSubheader,
  Tooltip,
} from "@mui/material";
import { Check } from "@phosphor-icons/react";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { FLOATING_CONTROLS_CLEARANCE_PX } from "./floatingControls";

/** A glyph the menu can draw beside a verb: any icon component that takes a
 *  size — Phosphor's, or lucide's when a menu folds a toolbar whose buttons
 *  already wear lucide (#12091), so the items carry the same icons. */
export type MenuGlyph = React.ComponentType<{
  size?: number | string;
  "aria-hidden"?: boolean;
}>;

export interface MenuAction {
  /** The verb. Single line; the surface truncates rather than wrapping. */
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  /** Why a disabled item is unavailable, in a tooltip on it (a constraint, per the copy rule).
   *  Shown only while the item is disabled. */
  disabledHint?: string;
  /** Reads in `--fp-danger` and sorts last, under a divider. */
  destructive?: boolean;
  /** Reserved for menus whose items are objects rather than actions. Icons are
   *  all-or-nothing per menu and default off: a glyph beside "Rename" decorates
   *  a verb, and a half-iconed menu reads as an accident. */
  icon?: MenuGlyph;
  /** Starts a new section. A divider means a change of object — never a
   *  visual breather. */
  dividerBefore?: boolean;
  /** Marks the value a choosing menu currently holds. A menu that picks one value and
   *  closes is still a menu; this is never multi-select. */
  current?: boolean;
  /** A quiet word after the label saying something true of that item now ("saved"). */
  note?: string;
  /** A quiet line under the label saying what the item makes, for an item whose name alone
   *  does not ("Documents, emails and notes on one tab"). The label stays its name. */
  description?: string;
  /** Starts a named section (a tab's name over its saved views, as the layout tab row's view
   *  menu does): a quiet heading above this item, and the
   *  divider a change of object already gets when something sits above it. */
  section?: string;
}

export interface MenuProps {
  anchorEl: HTMLElement | null;
  /** A right-click opens the menu at the pointer instead of at a control.
   *  Where the menu opens is the anchor, not styling, so it belongs here;
   *  a bottom sheet ignores it and pins to the bottom edge either way. */
  anchorPosition?: { top: number; left: number } | null;
  open: boolean;
  onClose: () => void;
  items: MenuAction[];
  /** Accessible name for the menu itself. */
  label?: string;
  /** A statement above the items — whose thing this is, when it changed —
   *  set in the secondary ink, parted from the verbs by a divider. Not a
   *  control. */
  head?: React.ReactNode;
  /** Which way the anchored menu opens: below the control (the default), or
   *  over it — the paper's bottom-right corner on the control's, no gap,
   *  never narrower than the control or the surface's floor, so a control on
   *  a footer is covered rather than left showing under a floating paper
   *  (#6559 round 13, Claude Design; "above with a gap" was the shape it
   *  replaced). It grows LEFT from the control (round 15): a footer's
   *  actions sit at its right end, so the free space is on the control's
   *  left, and a paper at the surface's floor read as too small when it was
   *  shrunk to a 96px control to spare the neighbours. A bottom sheet
   *  ignores it. */
  placement?: "below" | "over";
  /** Which edge of the trigger the menu lines up with: its right edge, growing left (the
   *  default, for a control at the end of a row), or its left edge, growing right, for a
   *  control at the start of one, such as a title, so the menu stays over the content. */
  align?: "start" | "end";
  /** Which item takes focus when the menu opens. A trigger opened with
   *  ArrowUp asks for the last, as a pop-up button does. */
  initialFocus?: "first" | "last";
  /** Hears that the menu has finished closing (its exit transition done and
   *  its focus trap gone). An item that moves focus somewhere of its own (a
   *  rename field it opens) does it here, with `restoreFocus` false: while the
   *  menu is still closing, its trap would pull the focus back and end that
   *  edit at once. */
  onClosed?: () => void;
  /** False when closing should leave focus where an item put it instead of
   *  returning it to where it was before the menu opened. */
  restoreFocus?: boolean;
  /** A fixed width for a menu of objects whose items carry a second line (the layout editor's
   *  Add tab menu, 320px): its rows get room, a 36px row for a name over its description and
   *  32px for a name alone, and its sections space apart. Never past the viewport. Without it
   *  a menu sizes to its verbs between the surface's floor and 320px. */
  width?: number;
}

/** One line of an item that ends in an ellipsis rather than wrapping. */
const oneLine: React.CSSProperties = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/** Below this the anchored menu becomes a bottom sheet: an anchored 30px-row
 *  menu is not a touch target. */
const SHEET_BELOW_PX = 600;

/** Watched rather than read once, so a menu left open across a rotation is
 *  not still wearing the presentation of the width it opened at. */
const useSheetPresentation = (): boolean => {
  const query = `(max-width: ${SHEET_BELOW_PX - 1}px)`;
  const [sheet, setSheet] = React.useState(
    () => window.matchMedia(query).matches
  );
  React.useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent): void =>
      setSheet(event.matches);
    setSheet(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [query]);
  return sheet;
};

const surfaceSx = {
  minWidth: 176,
  maxWidth: 320,
  borderRadius: "7px",
  border: "1px solid var(--fp-border)",
  backgroundColor: "var(--fp-surface)",
  // The one shadow this product draws. MUI's elevation is the single thing
  // that made these menus read as foreign.
  boxShadow: "var(--fp-lift)",
  backgroundImage: "none",
  padding: "4px",
};

const sheetSx = {
  ...surfaceSx,
  minWidth: 0,
  maxWidth: "none",
  left: "0 !important",
  right: 0,
  top: "auto !important",
  bottom: 0,
  width: "100%",
  borderRadius: "12px 12px 0 0",
  borderBottom: "none",
  // Clear of the app's floating buttons at the bottom right, which would
  // otherwise sit over a sheet's last rows (#11422 round 12).
  paddingBottom: `${FLOATING_CONTROLS_CLEARANCE_PX}px`,
};

/** A wide menu's rows: room for a name over its description, which a 30px verb row lacks. */
const roomyItemSx = (described: boolean, sheet: boolean) => ({
  minHeight: sheet ? 44 : described ? 36 : 32,
  height: "auto",
  padding: described ? "4px 10px" : "0 10px",
  gap: "8px",
});

const itemSx = (destructive: boolean, sheet: boolean) => ({
  minHeight: sheet ? 44 : 30,
  height: sheet ? 44 : 30,
  padding: "0 8px",
  borderRadius: "5px",
  fontSize: sheet ? 15 : 13,
  fontWeight: 400,
  lineHeight: 1.2,
  color: destructive ? "var(--fp-danger)" : "var(--fp-ink)",
  gap: 1.25,
  // No transition: a 30px row that fades on hover feels slow.
  transition: "none",
  "&:hover": { backgroundColor: "var(--fp-hover)" },
  // Hover and keyboard focus have to be told apart, because a keyboard user
  // moving through a menu the mouse is also resting in needs to see which one
  // is live. The ring is the app's focus ink at 2px (#11849 Round B, accessibility S5): the
  // strong hairline it replaced was about as faint as the hover wash it sat on.
  "&.Mui-focusVisible": {
    backgroundColor: "var(--fp-hover)",
    boxShadow: "inset 0 0 0 2px var(--fp-ink)",
  },
  "&:active": { backgroundColor: "var(--fp-ink-wash)" },
  "&.Mui-disabled": { opacity: 0.4 },
});

export const Menu: React.FC<MenuProps> = ({
  anchorEl,
  anchorPosition = null,
  open,
  onClose,
  items,
  label,
  head,
  placement = "below",
  align = "end",
  initialFocus = "first",
  restoreFocus = true,
  onClosed,
  width,
}) => {
  const roomy = width !== undefined;
  // A glyph column is all or nothing: when one item carries a glyph, the others keep its
  // column, so every name starts at the same place.
  // Only a wide menu of objects keeps the column; every other menu draws as it always has.
  const glyphColumn = roomy && items.some((item) => item.icon !== undefined);
  const edge = align === "start" ? "left" : "right";
  const sheet = useSheetPresentation();
  const reducedMotion = usePrefersReducedMotion();
  const listId = React.useId();
  const headId = `${listId}-head`;
  // Over its trigger the paper is at least the control's width, and never
  // under the surface's floor: the control must be covered, and growing
  // from the control's right corner keeps a paper wider than the control
  // off the actions beside it.
  const coverWidth =
    placement === "over" && open && !sheet && anchorEl
      ? Math.max(anchorEl.getBoundingClientRect().width, surfaceSx.minWidth)
      : surfaceSx.minWidth;
  const focusLast = (): void => {
    if (initialFocus !== "last") return;
    const rows = document
      .getElementById(listId)
      ?.querySelectorAll<HTMLElement>(
        '[role^="menuitem"]:not([aria-disabled="true"])'
      );
    rows?.[rows.length - 1]?.focus();
  };
  // Destructive last, under a divider, whatever order the call site passed:
  // the item that cannot be undone is not something a caller should be able to
  // put in the middle by accident.
  const ordered = [
    ...items.filter((item) => !item.destructive),
    ...items.filter((item) => item.destructive),
  ];
  // A menu whose items carry a current state is a choice among them.
  const choosing = items.some((entry) => entry.current !== undefined);
  return (
    <MuiMenu
      anchorEl={anchorEl}
      anchorReference={anchorPosition && !sheet ? "anchorPosition" : "anchorEl"}
      anchorPosition={anchorPosition ?? undefined}
      open={open}
      onClose={onClose}
      disableRestoreFocus={!restoreFocus}
      onClick={(event) => event.stopPropagation()}
      anchorOrigin={{ vertical: "bottom", horizontal: edge }}
      transformOrigin={{
        vertical: placement === "over" ? "bottom" : "top",
        horizontal: edge,
      }}
      marginThreshold={8}
      // The grow transition is motion the duration tokens cannot reach; it stands down for a
      // reader who asked for reduced motion (#11849 Round B).
      transitionDuration={reducedMotion ? 0 : "auto"}
      TransitionProps={{ onEntered: focusLast, onExited: onClosed }}
      MenuListProps={{
        id: listId,
        dense: true,
        // A disabled item's label can carry why it is unavailable ("administrators only"), so
        // the arrow keys stop on it and a screen reader reads it, still disabled (#11849 Round B).
        disabledItemsFocusable: true,
        "aria-label": label,
        // The head describes the menu; arrow keys never land on it.
        "aria-describedby":
          head !== undefined && head !== null ? headId : undefined,
        sx: { padding: 0 },
      }}
      slotProps={{
        paper: {
          sx: sheet
            ? sheetSx
            : roomy
              ? {
                  ...surfaceSx,
                  width,
                  minWidth: 0,
                  maxWidth: "calc(100vw - 16px)",
                  padding: "6px",
                }
              : { ...surfaceSx, minWidth: coverWidth },
        },
      }}
    >
      {/* Flat children, never a fragment: MUI's MenuList reads its children
          to pick the item that takes focus, and a fragment in that list is
          what it would try to focus. ListSubheader is marked to be skipped
          (muiSkipListHighlight), so focus lands on the first item. */}
      {head !== undefined && head !== null && (
        <ListSubheader
          key="head"
          id={headId}
          component="li"
          role="presentation"
          disableSticky
          sx={{
            padding: "6px 12px 8px",
            fontSize: 12,
            lineHeight: "16px",
            color: "var(--fp-ink-3)",
            backgroundColor: "transparent",
          }}
        >
          {head}
        </ListSubheader>
      )}
      {head !== undefined && head !== null && (
        <Divider
          key="head-divider"
          sx={{ margin: "4px -4px", borderColor: "var(--fp-border)" }}
        />
      )}
      {ordered.flatMap((item, index) => {
        const Glyph = item.icon;
        // A divider parts the destructive block from what is above it; a
        // menu that opens on its destructive item has nothing to part.
        const firstDestructive =
          index > 0 &&
          item.destructive === true &&
          ordered.findIndex((other) => other.destructive) === index;
        const hint = item.disabled ? item.disabledHint : undefined;
        // A wide menu keeps the whole row for the name: the item's note leads its second line
        // ("Added as Notes · Interaction type is Email") instead of crowding the name.
        const secondLine =
          roomy && item.note
            ? [item.note, item.description].filter(Boolean).join(" · ")
            : item.description;
        const hintId = `${listId}-hint-${index}`;
        const menuRow = (
          <MenuItem
            key={`${index}-${item.label}`}
            disabled={item.disabled}
            role={choosing ? "menuitemradio" : "menuitem"}
            aria-checked={choosing ? item.current === true : undefined}
            aria-label={
              item.note
                ? `${item.label}, ${item.note}`
                : hint || item.description
                  ? item.label
                  : undefined
            }
            // The reason is the item's description whether or not its tooltip is open, so a
            // keyboard or screen-reader user, who never hovers, hears it on the item.
            aria-describedby={hint ? hintId : undefined}
            onClick={() => {
              // A hinted disabled item takes the pointer, for its tooltip, and so a click.
              if (item.disabled) return;
              onClose();
              item.onSelect();
            }}
            sx={{
              ...itemSx(item.destructive === true, sheet),
              ...(roomy
                ? roomyItemSx(Boolean(secondLine), sheet)
                : item.description !== undefined
                  ? { height: "auto", minHeight: 36 }
                  : {}),
              // The tooltip needs the pointer, which a disabled item otherwise refuses.
              ...(hint
                ? {
                    "&.Mui-disabled": {
                      opacity: 0.4,
                      pointerEvents: "auto",
                      cursor: "default",
                    },
                    "&.Mui-disabled:hover": { backgroundColor: "transparent" },
                  }
                : {}),
            }}
          >
            {Glyph ? (
              <Glyph size={16} aria-hidden />
            ) : (
              glyphColumn && (
                <span aria-hidden style={{ width: 16, flex: "none" }} />
              )
            )}
            {/* The name, and under it what the item is, each one line that ends in an ellipsis
                rather than wrapping. An item of one line is the name alone, as it always was. */}
            {secondLine ? (
              <span style={{ minWidth: 0, flex: "1 1 auto" }}>
                <span style={oneLine}>{item.label}</span>
                <span
                  data-menu-description=""
                  style={{
                    ...oneLine,
                    color: "var(--fp-ink-3)",
                    fontSize: 12,
                    lineHeight: "16px",
                  }}
                >
                  {secondLine}
                </span>
              </span>
            ) : (
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {item.label}
              </span>
            )}
            {item.note && !roomy && (
              <span style={{ color: "var(--fp-ink-3)", flex: "none" }}>
                {item.note}
              </span>
            )}
            {hint && (
              <span id={hintId} className="sr-only">
                {hint}
              </span>
            )}
            {choosing && (
              <span
                aria-hidden
                data-current={item.current ? "true" : undefined}
                style={{
                  marginLeft: "auto",
                  display: "inline-flex",
                  width: 16,
                  flex: "none",
                  visibility: item.current ? "visible" : "hidden",
                }}
              >
                <Check size={14} />
              </span>
            )}
          </MenuItem>
        );
        // Described, not named, by its hint: the item keeps its verb as its name.
        const row = hint ? (
          <Tooltip
            key={`${index}-${item.label}`}
            title={hint}
            placement="left"
            describeChild
          >
            {menuRow}
          </Tooltip>
        ) : (
          menuRow
        );
        // A divider above the destructive block when something sits above it,
        // and wherever the call site marks a change of object.
        const divider =
          (index > 0 && (item.dividerBefore || item.section !== undefined)) ||
          firstDestructive;
        // A section's name sits above its first item, quiet and not a stop for the arrow keys.
        const heading = item.section !== undefined && (
          <ListSubheader
            key={`${item.label}-section`}
            role="presentation"
            disableSticky
            sx={{
              lineHeight: roomy ? "16px" : "24px",
              // A wide menu spaces its sections: room above every heading but the first.
              padding: roomy ? `${index > 0 ? 10 : 4}px 10px 4px` : "0 8px",
              backgroundColor: "transparent",
              color: "var(--fp-ink-2)",
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "var(--fp-track-caps)",
              textTransform: "uppercase",
            }}
          >
            {item.section}
          </ListSubheader>
        );
        return [
          ...(divider
            ? [
                <Divider
                  key={`${item.label}-divider`}
                  sx={{
                    // Between a wide menu's sections it parts them; inside a section it sets
                    // off the item that makes something from the things listed above it.
                    margin: roomy
                      ? item.section !== undefined
                        ? "4px -6px"
                        : "4px 10px"
                      : "4px -4px",
                    borderColor: "var(--fp-border)",
                  }}
                />,
              ]
            : []),
          ...(heading ? [heading] : []),
          row,
        ];
      })}
    </MuiMenu>
  );
};

// The menu's own paper, sheet and presentation switch, for the one menu that
// cannot be a list of actions: the claims intake's document type menu
// (#11422 round 12) has a filter field and a second level, which this wrapper
// deliberately refuses. It composes these rather than restyling MUI, so its
// papers are this menu's papers and cannot drift from them.
export {
  surfaceSx as menuSurfaceSx,
  sheetSx as menuSheetSx,
  useSheetPresentation as useMenuSheetPresentation,
};
